import { useState, useEffect, useCallback } from "react";
import {
  getSchedulerStatus,
  getSchedulerConfig,
  updateSchedulerConfig,
  triggerSchedulerJob,
  listSchedulerRuns,
  formatCronToTime,
  timeToCron,
  formatDuration,
  getJobTypeDisplayName,
  getJobTypeDescription,
  getStatusColor,
  type SchedulerStatus,
  type SchedulerConfig,
  type SchedulerRun,
  type JobType,
  type RunStatus,
  type StaleDataPolicy,
} from "../../services/schedulerAdminService";
import { SchedulerRunDetails } from "./SchedulerRunDetails";

export function SchedulerPanel() {
  const [status, setStatus] = useState<SchedulerStatus | null>(null);
  const [config, setConfig] = useState<SchedulerConfig | null>(null);
  const [runs, setRuns] = useState<SchedulerRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [editConfig, setEditConfig] = useState<Partial<SchedulerConfig>>({});
  const [saving, setSaving] = useState(false);
  const [triggering, setTriggering] = useState<JobType | null>(null);
  const [selectedRun, setSelectedRun] = useState<SchedulerRun | null>(null);
  const [runsPage, setRunsPage] = useState(1);
  const [runsTotalPages, setRunsTotalPages] = useState(1);
  const [runsFilter, setRunsFilter] = useState<{
    jobType?: JobType;
    status?: RunStatus;
  }>({});
  const [expanded, setExpanded] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [statusData, configData, runsData] = await Promise.all([
        getSchedulerStatus(),
        getSchedulerConfig(),
        listSchedulerRuns({
          page: runsPage,
          limit: 10,
          ...runsFilter,
        }),
      ]);

      setStatus(statusData);
      setConfig(configData);
      setRuns(runsData.runs);
      setRunsTotalPages(runsData.total_pages);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load scheduler data");
    } finally {
      setLoading(false);
    }
  }, [runsPage, runsFilter]);

  useEffect(() => {
    if (expanded) {
      loadData();
    }
  }, [expanded, loadData]);

  // Auto-refresh every 30 seconds when expanded
  useEffect(() => {
    if (!expanded) return;

    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [expanded, loadData]);

  const handleEditToggle = () => {
    if (editMode) {
      // Cancel editing
      setEditConfig({});
      setEditMode(false);
    } else {
      // Start editing
      if (config) {
        setEditConfig({
          sync_cron: config.sync_cron,
          execution_cron: config.execution_cron,
          snapshot_cron: config.snapshot_cron,
          enabled: config.enabled,
          dry_run: config.dry_run,
          stale_data_policy: config.stale_data_policy,
        });
      }
      setEditMode(true);
    }
  };

  const handleSaveConfig = async () => {
    try {
      setSaving(true);
      setError(null);

      const updates: Record<string, unknown> = {};
      if (
        editConfig.sync_cron &&
        editConfig.sync_cron !== config?.sync_cron
      ) {
        updates.sync_cron = editConfig.sync_cron;
      }
      if (
        editConfig.execution_cron &&
        editConfig.execution_cron !== config?.execution_cron
      ) {
        updates.execution_cron = editConfig.execution_cron;
      }
      if (
        editConfig.snapshot_cron &&
        editConfig.snapshot_cron !== config?.snapshot_cron
      ) {
        updates.snapshot_cron = editConfig.snapshot_cron;
      }
      if (editConfig.enabled !== undefined && editConfig.enabled !== config?.enabled) {
        updates.enabled = editConfig.enabled;
      }
      if (editConfig.dry_run !== undefined && editConfig.dry_run !== config?.dry_run) {
        updates.dry_run = editConfig.dry_run;
      }
      if (editConfig.stale_data_policy !== undefined && editConfig.stale_data_policy !== config?.stale_data_policy) {
        updates.stale_data_policy = editConfig.stale_data_policy;
      }

      if (Object.keys(updates).length > 0) {
        await updateSchedulerConfig(updates);
      }

      setEditMode(false);
      setEditConfig({});
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const handleTriggerJob = async (jobType: JobType) => {
    try {
      setTriggering(jobType);
      setError(null);
      await triggerSchedulerJob(jobType);
      // Wait a moment then refresh to show the running job
      setTimeout(loadData, 500);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to trigger job");
    } finally {
      setTriggering(null);
    }
  };

  const handleTimeChange = (field: "sync_cron" | "execution_cron" | "snapshot_cron", time: string) => {
    const cron = timeToCron(time);
    if (cron) {
      setEditConfig((prev) => ({ ...prev, [field]: cron }));
    }
  };

  return (
    <div className="border border-surface-700/50 rounded-xl overflow-hidden">
      {/* Header - clickable to expand/collapse */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 bg-surface-800/50 hover:bg-surface-700/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
            <svg
              className="w-5 h-5 text-amber-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div className="text-left">
            <h3 className="text-lg font-semibold text-surface-100">
              Scheduler Management
            </h3>
            <p className="text-sm text-surface-400">
              Configure and monitor scheduled jobs
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {status && (
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium ${
                status.running
                  ? "bg-green-500/20 text-green-400"
                  : "bg-red-500/20 text-red-400"
              }`}
            >
              {status.running ? "Running" : "Stopped"}
            </span>
          )}
          <svg
            className={`w-5 h-5 text-surface-400 transition-transform ${
              expanded ? "rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </button>

      {/* Expanded Content */}
      {expanded && (
        <div className="p-4 space-y-6 border-t border-surface-700/50">
          {loading && !status ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent"></div>
            </div>
          ) : error && !status ? (
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-red-400">{error}</p>
              <button
                onClick={loadData}
                className="mt-2 text-sm text-red-300 hover:text-red-200 underline"
              >
                Retry
              </button>
            </div>
          ) : (
            <>
              {/* Error banner */}
              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              {/* Status Overview */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {(["sync", "execution", "snapshot"] as JobType[]).map(
                  (jobType) => (
                    <JobCard
                      key={jobType}
                      jobType={jobType}
                      status={status}
                      onTrigger={() => handleTriggerJob(jobType)}
                      triggering={triggering === jobType}
                      disabled={triggering !== null || !status?.enabled}
                    />
                  )
                )}
              </div>

              {/* Configuration Section */}
              <div className="bg-surface-800/30 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-md font-semibold text-surface-100">
                    Schedule Configuration
                  </h4>
                  <div className="flex gap-2">
                    {editMode && (
                      <button
                        onClick={handleSaveConfig}
                        disabled={saving}
                        className="btn-primary text-sm px-3 py-1"
                      >
                        {saving ? "Saving..." : "Save"}
                      </button>
                    )}
                    <button
                      onClick={handleEditToggle}
                      className="btn-secondary text-sm px-3 py-1"
                    >
                      {editMode ? "Cancel" : "Edit"}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <ConfigField
                    label="Sync Time"
                    value={config?.sync_cron || ""}
                    displayValue={formatCronToTime(config?.sync_cron || "")}
                    editMode={editMode}
                    editValue={editConfig.sync_cron || config?.sync_cron || ""}
                    onChange={(time) => handleTimeChange("sync_cron", time)}
                    description="Portfolio sync"
                  />
                  <ConfigField
                    label="Execution Time"
                    value={config?.execution_cron || ""}
                    displayValue={formatCronToTime(config?.execution_cron || "")}
                    editMode={editMode}
                    editValue={
                      editConfig.execution_cron || config?.execution_cron || ""
                    }
                    onChange={(time) => handleTimeChange("execution_cron", time)}
                    description="Trade execution"
                  />
                  <ConfigField
                    label="Snapshot Time"
                    value={config?.snapshot_cron || ""}
                    displayValue={formatCronToTime(config?.snapshot_cron || "")}
                    editMode={editMode}
                    editValue={
                      editConfig.snapshot_cron || config?.snapshot_cron || ""
                    }
                    onChange={(time) => handleTimeChange("snapshot_cron", time)}
                    description="Position snapshot"
                  />
                </div>

                <div className="mt-4 flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={
                        editMode
                          ? editConfig.enabled ?? config?.enabled
                          : config?.enabled
                      }
                      onChange={(e) =>
                        editMode &&
                        setEditConfig((prev) => ({
                          ...prev,
                          enabled: e.target.checked,
                        }))
                      }
                      disabled={!editMode}
                      className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-primary-500 focus:ring-primary-500 focus:ring-offset-0"
                    />
                    <span className="text-sm text-surface-300">
                      Scheduler Enabled
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={
                        editMode
                          ? editConfig.dry_run ?? config?.dry_run
                          : config?.dry_run
                      }
                      onChange={(e) =>
                        editMode &&
                        setEditConfig((prev) => ({
                          ...prev,
                          dry_run: e.target.checked,
                        }))
                      }
                      disabled={!editMode}
                      className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-yellow-500 focus:ring-yellow-500 focus:ring-offset-0"
                    />
                    <span className="text-sm text-surface-300">
                      Dry Run Mode
                    </span>
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-surface-300">
                      Stale Data Policy:
                    </span>
                    <select
                      value={
                        editMode
                          ? editConfig.stale_data_policy ?? config?.stale_data_policy ?? "warn"
                          : config?.stale_data_policy ?? "warn"
                      }
                      onChange={(e) =>
                        editMode &&
                        setEditConfig((prev) => ({
                          ...prev,
                          stale_data_policy: e.target.value as StaleDataPolicy,
                        }))
                      }
                      disabled={!editMode}
                      className="input-field text-sm px-2 py-1 w-24"
                    >
                      <option value="block">Block</option>
                      <option value="warn">Warn</option>
                      <option value="ignore">Ignore</option>
                    </select>
                  </div>
                  <span className="text-sm text-surface-500">
                    Timezone: {config?.timezone || status?.timezone || "America/New_York"}
                  </span>
                </div>
              </div>

              {/* Run History Section */}
              <div className="bg-surface-800/30 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-md font-semibold text-surface-100">
                    Run History
                  </h4>
                  <div className="flex gap-2">
                    <select
                      value={runsFilter.jobType || ""}
                      onChange={(e) =>
                        setRunsFilter((prev) => ({
                          ...prev,
                          jobType: (e.target.value as JobType) || undefined,
                        }))
                      }
                      className="input-field text-sm px-2 py-1"
                    >
                      <option value="">All Jobs</option>
                      <option value="sync">Sync</option>
                      <option value="execution">Execution</option>
                      <option value="snapshot">Snapshot</option>
                      <option value="price_refresh_hourly">Price Refresh (Hourly)</option>
                      <option value="price_refresh_pretrade">Price Refresh (Pre-Trade)</option>
                      <option value="price_refresh_afterhours">Price Refresh (After Hours)</option>
                      <option value="cache_compare">Cache Comparison</option>
                    </select>
                    <select
                      value={runsFilter.status || ""}
                      onChange={(e) =>
                        setRunsFilter((prev) => ({
                          ...prev,
                          status: (e.target.value as RunStatus) || undefined,
                        }))
                      }
                      className="input-field text-sm px-2 py-1"
                    >
                      <option value="">All Status</option>
                      <option value="running">Running</option>
                      <option value="completed">Completed</option>
                      <option value="failed">Failed</option>
                    </select>
                    <button
                      onClick={loadData}
                      className="btn-secondary text-sm px-3 py-1"
                      disabled={loading}
                    >
                      {loading ? "..." : "Refresh"}
                    </button>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-sm text-surface-400 border-b border-surface-700/50">
                        <th className="pb-2 pr-4">Job</th>
                        <th className="pb-2 pr-4">Trigger</th>
                        <th className="pb-2 pr-4">Status</th>
                        <th className="pb-2 pr-4">Started</th>
                        <th className="pb-2 pr-4">Duration</th>
                        <th className="pb-2"></th>
                      </tr>
                    </thead>
                    <tbody className="text-sm">
                      {runs.length === 0 ? (
                        <tr>
                          <td
                            colSpan={6}
                            className="py-8 text-center text-surface-500"
                          >
                            No runs found
                          </td>
                        </tr>
                      ) : (
                        runs.map((run) => (
                          <RunRow
                            key={run.id}
                            run={run}
                            onSelect={() => setSelectedRun(run)}
                          />
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {runsTotalPages > 1 && (
                  <div className="mt-4 flex items-center justify-center gap-2">
                    <button
                      onClick={() => setRunsPage((p) => Math.max(1, p - 1))}
                      disabled={runsPage === 1}
                      className="btn-secondary text-sm px-3 py-1 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-surface-400">
                      Page {runsPage} of {runsTotalPages}
                    </span>
                    <button
                      onClick={() =>
                        setRunsPage((p) => Math.min(runsTotalPages, p + 1))
                      }
                      disabled={runsPage === runsTotalPages}
                      className="btn-secondary text-sm px-3 py-1 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Run Details Modal */}
      {selectedRun && (
        <SchedulerRunDetails
          run={selectedRun}
          onClose={() => setSelectedRun(null)}
        />
      )}
    </div>
  );
}

// Job Card Component
function JobCard({
  jobType,
  status,
  onTrigger,
  triggering,
  disabled,
}: {
  jobType: JobType;
  status: SchedulerStatus | null;
  onTrigger: () => void;
  triggering: boolean;
  disabled: boolean;
}) {
  const getNextTime = () => {
    switch (jobType) {
      case "sync":
        return status?.next_sync_time;
      case "execution":
        return status?.next_execution_time;
      case "snapshot":
        return status?.next_snapshot_time;
    }
  };

  const getLastTime = () => {
    switch (jobType) {
      case "sync":
        return status?.last_sync_run;
      case "execution":
        return status?.last_execution_run;
      case "snapshot":
        return status?.last_snapshot_run;
    }
  };

  const isRunning =
    status?.current_job_type === jobType ||
    status?.running_jobs?.some((j) => j.job_type === jobType);

  const formatTime = (dateStr: string | undefined) => {
    if (!dateStr) return "-";
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime()) || date.getFullYear() === 1) return "-";
      return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } catch {
      return "-";
    }
  };

  const getJobIcon = () => {
    switch (jobType) {
      case "sync":
        return (
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        );
      case "execution":
        return (
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 10V3L4 14h7v7l9-11h-7z"
          />
        );
      case "snapshot":
        return (
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
          />
        );
    }
  };

  const jobColors: Record<string, string> = {
    sync: "blue",
    execution: "amber",
    snapshot: "purple",
    price_refresh_hourly: "teal",
    price_refresh_pretrade: "orange",
    price_refresh_afterhours: "indigo",
  };
  const color = jobColors[jobType] || "gray";

  return (
    <div className="bg-surface-800/50 rounded-lg p-4 border border-surface-700/50">
      <div className="flex items-center gap-3 mb-3">
        <div
          className={`w-8 h-8 rounded-lg bg-${color}-500/20 flex items-center justify-center`}
        >
          <svg
            className={`w-4 h-4 text-${color}-400`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            {getJobIcon()}
          </svg>
        </div>
        <div className="flex-1">
          <h5 className="text-sm font-medium text-surface-100">
            {getJobTypeDisplayName(jobType)}
          </h5>
          <p className="text-xs text-surface-500">
            {getJobTypeDescription(jobType)}
          </p>
        </div>
        {isRunning && (
          <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-500/20 text-yellow-400 animate-pulse">
            Running
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs mb-3">
        <div>
          <span className="text-surface-500">Next:</span>
          <span className="ml-1 text-surface-300">{formatTime(getNextTime())}</span>
        </div>
        <div>
          <span className="text-surface-500">Last:</span>
          <span className="ml-1 text-surface-300">{formatTime(getLastTime())}</span>
        </div>
      </div>

      <button
        onClick={onTrigger}
        disabled={disabled || isRunning}
        className="w-full btn-secondary text-sm py-1.5 disabled:opacity-50"
      >
        {triggering ? (
          <span className="flex items-center justify-center gap-2">
            <div className="animate-spin rounded-full h-3 w-3 border border-current border-t-transparent"></div>
            Triggering...
          </span>
        ) : (
          "Trigger Now"
        )}
      </button>
    </div>
  );
}

// Config Field Component
function ConfigField({
  label,
  value,
  displayValue,
  editMode,
  editValue,
  onChange,
  description,
}: {
  label: string;
  value: string;
  displayValue: string;
  editMode: boolean;
  editValue: string;
  onChange: (time: string) => void;
  description: string;
}) {
  // Parse cron to time for input
  const parseToTime = (cron: string) => {
    const parts = cron.split(" ");
    if (parts.length < 5) return "14:30"; // default
    const minute = parts[0].padStart(2, "0");
    const hour = parts[1].padStart(2, "0");
    return `${hour}:${minute}`;
  };

  return (
    <div>
      <label className="block text-xs text-surface-500 mb-1">{label}</label>
      {editMode ? (
        <input
          type="time"
          value={parseToTime(editValue)}
          onChange={(e) => onChange(e.target.value)}
          className="input-field w-full text-sm"
        />
      ) : (
        <div className="text-surface-100 font-medium">{displayValue}</div>
      )}
      <div className="text-xs text-surface-500 mt-0.5">{description}</div>
      {!editMode && (
        <div className="text-xs text-surface-600 font-mono">{value}</div>
      )}
    </div>
  );
}

// Run Row Component
function RunRow({
  run,
  onSelect,
}: {
  run: SchedulerRun;
  onSelect: () => void;
}) {
  const statusColor = getStatusColor(run.status);
  const colorClasses = {
    green: "bg-green-500/20 text-green-400",
    yellow: "bg-yellow-500/20 text-yellow-400",
    red: "bg-red-500/20 text-red-400",
    gray: "bg-surface-600/20 text-surface-400",
  };

  return (
    <tr className="border-b border-surface-700/30 hover:bg-surface-700/20">
      <td className="py-2 pr-4">
        <span className="font-medium text-surface-200">
          {getJobTypeDisplayName(run.job_type)}
        </span>
      </td>
      <td className="py-2 pr-4">
        <span
          className={`px-1.5 py-0.5 rounded text-xs ${
            run.trigger_type === "manual"
              ? "bg-blue-500/20 text-blue-400"
              : "bg-surface-600/20 text-surface-400"
          }`}
        >
          {run.trigger_type}
        </span>
      </td>
      <td className="py-2 pr-4">
        <span
          className={`px-1.5 py-0.5 rounded text-xs ${colorClasses[statusColor]}`}
        >
          {run.status}
        </span>
      </td>
      <td className="py-2 pr-4 text-surface-400">
        {new Date(run.started_at).toLocaleString()}
      </td>
      <td className="py-2 pr-4 text-surface-400">
        {formatDuration(run.duration_ms)}
      </td>
      <td className="py-2">
        <button
          onClick={onSelect}
          className="text-primary-400 hover:text-primary-300 text-xs"
        >
          Details
        </button>
      </td>
    </tr>
  );
}
